import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react'
import * as echarts from 'echarts'
import { safeChartInit } from '../../../../../utils/echarts'
import type { TFunction } from 'i18next'
import type { PopulationData } from '../../demographicsService'

interface HouseholdCompositionChartProps {
  data: PopulationData
  height?: number
  t: TFunction
}

export interface HouseholdCompositionChartRef {
  exportChart: () => string | null
}

export const HouseholdCompositionChart = forwardRef<
  HouseholdCompositionChartRef,
  HouseholdCompositionChartProps
>(({ data, height = 350, t }, ref) => {
  const chartRef = useRef<HTMLDivElement>(null)
  const chartInstance = useRef<echarts.ECharts | null>(null)

  useImperativeHandle(ref, () => ({
    exportChart: () => {
      if (chartInstance.current) {
        return chartInstance.current.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#fff',
        })
      }
      return null
    },
  }))

  useEffect(() => {
    if (!chartRef.current || !data) return

    // Early return if data is invalid or missing required fields
    if (
      typeof data.total_households_number !== 'number' ||
      data.total_households_number <= 0
    ) {
      return
    }

    // Initialize chart
    chartInstance.current = safeChartInit(chartRef.current)

    // Prepare household data
    const householdData = [
      {
        name: t('charts.householdComposition.legend.1person'),
        value: data.total_households_1,
        category: 'studio',
        color: '#3b82f6',
      },
      {
        name: t('charts.householdComposition.legend.2people'),
        value: data.total_households_2,
        category: 'couple',
        color: '#10b981',
      },
      {
        name: t('charts.householdComposition.legend.3people'),
        value: data.total_households_3,
        category: 'small_family',
        color: '#f59e0b',
      },
      {
        name: t('charts.householdComposition.legend.4people'),
        value: data.total_households_4,
        category: 'family',
        color: '#ef4444',
      },
      {
        name: t('charts.householdComposition.legend.5people'),
        value: data.total_households_5,
        category: 'large_family',
        color: '#8b5cf6',
      },
      {
        name: t('charts.householdComposition.legend.6plusPeople'),
        value: data.total_households_6_plus,
        category: 'extended_family',
        color: '#06b6d4',
      },
    ]

    // Calculate percentages and real estate implications
    const totalHouseholds = data.total_households_number
    const dataWithPercentages = householdData.map((item) => ({
      ...item,
      percentage:
        totalHouseholds > 0
          ? ((item.value / totalHouseholds) * 100).toFixed(1)
          : '0',
    }))

    // Get real estate recommendations
    const getPropertyRecommendation = (category: string) => {
      switch (category) {
        case 'studio':
          return t('charts.householdComposition.recommendations.studio')
        case 'couple':
          return t('charts.householdComposition.recommendations.couple')
        case 'small_family':
          return t('charts.householdComposition.recommendations.smallFamily')
        case 'family':
          return t('charts.householdComposition.recommendations.family')
        case 'large_family':
          return t('charts.householdComposition.recommendations.largeFamily')
        case 'extended_family':
          return t('charts.householdComposition.recommendations.extendedFamily')
        default:
          return t('charts.householdComposition.recommendations.mixed')
      }
    }

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'item',
        confine: true,
        formatter: function (params: any) {
          const item = params.data
          const recommendation = getPropertyRecommendation(item.category)
          return `
            <div style="text-align: left;">
              <strong>${item.name} ${t('charts.householdComposition.tooltips.households')}</strong><br/>
              <span style="color: ${item.color};">${t('charts.householdComposition.tooltips.count')}: ${item.value.toLocaleString()}</span><br/>
              <span style="color: ${item.color};">${t('charts.householdComposition.tooltips.percentage')}: ${item.percentage}%</span><br/>
              <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #e5e7eb;">
                <strong>${t('charts.householdComposition.tooltips.propertyDemand')}:</strong><br/>
                <span style="color: #6b7280;">${recommendation}</span>
              </div>
            </div>
          `
        },
      },
      legend: {
        orient: 'horizontal',
        bottom: 0,
        left: 'center',
        itemGap: 20,
        selectedMode: false,
        textStyle: {
          fontSize: 12,
        },
        formatter: function (name: string) {
          const item = dataWithPercentages.find((d) => d.name === name)
          return `${name} (${item?.percentage}%)`
        },
      },
      series: [
        {
          name: t('charts.series.householdComposition'),
          type: 'pie',
          radius: ['30%', '70%'],
          center: ['50%', '40%'],
          avoidLabelOverlap: false,
          selectedMode: false,
          itemStyle: {
            borderRadius: 8,
            borderColor: '#fff',
            borderWidth: 2,
          },
          label: {
            show: true,
            position: 'outside',
            formatter: function (params: any) {
              return `${params.data.percentage}%`
            },
            fontSize: 12,
            fontWeight: 'bold',
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 14,
              fontWeight: 'bold',
            },
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
          labelLine: {
            show: true,
            length: 15,
            length2: 10,
          },
          data: dataWithPercentages.map((item) => ({
            name: item.name,
            value: item.value,
            category: item.category,
            percentage: item.percentage,
            itemStyle: {
              color: item.color,
            },
          })),
        },
      ],
    }

    chartInstance.current.setOption(option)

    // Handle resize
    const handleResize = () => {
      chartInstance.current?.resize()
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      chartInstance.current?.dispose()
    }
  }, [data, height])

  return (
    <div
      ref={chartRef}
      style={{
        width: '100%',
        height: `${height}px`,
        minHeight: '300px',
      }}
    />
  )
})

HouseholdCompositionChart.displayName = 'HouseholdCompositionChart'
